import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const CapitalLetterDetectiveGame = ({ gameData, onComplete }) => {
    const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
    const [shuffledSentences, setShuffledSentences] = useState([]);
    const [clickedWords, setClickedWords] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [showHints, setShowHints] = useState(false);

    useEffect(() => {
        if (gameData?.sentences) {
            setShuffledSentences([...gameData.sentences].sort(() => Math.random() - 0.5));
            setCurrentSentenceIndex(0);
        }
    }, [gameData]);

    const handleWordClick = (wordIndex) => {
        if (feedback) return;
        triggerHaptic(25);
        if (clickedWords.includes(wordIndex)) {
            setClickedWords(clickedWords.filter(i => i !== wordIndex));
        } else {
            setClickedWords([...clickedWords, wordIndex]);
        }
    };

    const checkAnswer = () => {
        if (feedback || clickedWords.length === 0) return;
        const correctWords = shuffledSentences[currentSentenceIndex].words
            .map((word, index) => word.needs_capital ? index : null)
            .filter(i => i !== null);

        const isCorrect =
            clickedWords.length === correctWords.length &&
            clickedWords.every(i => correctWords.includes(i));

        if (isCorrect) {
            setFeedback('correct');
            const points = (gameData.points_per_word || 10) * correctWords.length;
            const newScore = score + points;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentSentenceIndex + 1 >= shuffledSentences.length) {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                } else {
                    setCurrentSentenceIndex(prev => prev + 1);
                    setClickedWords([]);
                    setFeedback(null);
                }
            }, 4000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => {
                setFeedback(null);
            }, 1500);
        }
    };

    if (!gameData || shuffledSentences.length === 0) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-slate-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🕵️‍♂️</div>
                <h3 className="text-base md:text-3xl font-black text-slate-900 tracking-tighter uppercase">Ruošiami įrodymai...</h3>
            </div>
        );
    }

    const currentSentence = shuffledSentences[currentSentenceIndex];
    const progress = ((currentSentenceIndex) / shuffledSentences.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-slate-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-slate-900/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-200 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🕵️‍♂️
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Didžiųjų Raidžių Detektyvas
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žinios</p>
                        <p className="text-sm md:text-2xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Byla</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentSentenceIndex + 1}/{shuffledSentences.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-slate-700 via-indigo-600 to-slate-900 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(30,41,59,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-12">
            {/* Instruction & Investigation Tools */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-4 md:p-8 border-white/40 flex items-center gap-4 shadow-xl bg-white/40 backdrop-blur-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4 text-4xl md:text-6xl opacity-[0.05] select-none pointer-events-none">📜</div>
                    <div className="text-4xl md:text-6xl animate-float drop-shadow-xl">{currentSentence.emoji}</div>
                    <div className="flex-1">
                        <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-1">Bylos Suvestinė</h4>
                        <p className="text-sm md:text-xl text-slate-700 font-bold italic leading-tight font-serif">"{currentSentence.hint}"</p>
                    </div>
                </div>

                <button 
                    onClick={() => { triggerHaptic(20); setShowHints(!showHints); }}
                    className={`
                        premium-glass rounded-xl md:rounded-[2.5rem] p-4 md:p-8 border-white/40 flex items-center justify-center gap-4 shadow-xl transition-all duration-500
                        ${showHints ? 'bg-amber-500 text-white border-amber-400' : 'bg-white hover:bg-slate-50 text-slate-700'}
                    `}
                >
                    <div className="text-2xl md:text-4xl">{showHints ? '👁️' : '💡'}</div>
                    <span className="text-sm md:text-xl font-black uppercase tracking-widest">
                        {showHints ? 'Slėpti Įkalčius' : 'Rodyti Įkalčius'}
                    </span>
                </button>
            </div>

            {/* Main Investigation Stage */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-14 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden flex flex-col items-center min-h-[400px] justify-center">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.08)_0%,transparent_80%)] pointer-events-none" />
                <div className="absolute top-0 left-0 p-4 md:p-8 text-6xl md:text-8xl opacity-[0.05] select-none pointer-events-none text-white">🕵️‍♂️</div>
                
                <h4 className="text-[8px] md:text-[10px] font-black text-indigo-400 uppercase tracking-[0.5em] mb-6 md:mb-12 text-center">Pažymėk Įtartinus Žodžius</h4>

                <div className="flex flex-wrap gap-2 md:gap-4 justify-center relative z-10 w-full max-w-5xl">
                    {currentSentence.words.map((wordData, index) => {
                        const isClicked = clickedWords.includes(index);
                        const isCorrectInFeed = feedback === 'correct' && wordData.needs_capital;
                        const isWrongInFeed = feedback === 'wrong' && isClicked && !wordData.needs_capital;

                        return (
                            <button
                                key={index}
                                onClick={() => handleWordClick(index)}
                                disabled={!!feedback}
                                className={`
                                    relative px-4 py-3 md:px-10 md:py-6 rounded-xl md:rounded-[2.5rem] font-black text-sm md:text-3xl transition-all duration-500 border-b-4 md:border-b-[10px]
                                    ${isCorrectInFeed
                                        ? 'bg-emerald-500 text-white scale-110 shadow-[0_0_50px_rgba(16,185,129,0.5)] border-emerald-700'
                                        : isWrongInFeed
                                            ? 'bg-rose-500 text-white animate-shake shadow-[0_0_50px_rgba(244,63,94,0.5)] border-rose-700'
                                            : isClicked
                                                ? 'bg-indigo-600 text-white shadow-2xl scale-110 border-indigo-800'
                                                : 'bg-white text-slate-800 border-slate-200 hover:scale-[1.02] active:translate-y-2 active:border-b-0'}
                                    disabled:cursor-default
                                `}
                            >
                                <span className="tracking-tighter uppercase">{wordData.word}</span>
                                {isCorrectInFeed && (
                                    <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 w-6 h-6 md:w-12 md:h-12 bg-white text-emerald-600 rounded-full flex items-center justify-center text-xs md:text-2xl shadow-xl border-2 md:border-4 border-emerald-100 animate-pop-in">✓</div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Forensic Feedback & Insights Stage */}
            {feedback && (
                <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl animate-pop-in flex flex-col items-center">
                    <div className="w-full max-w-5xl space-y-6 md:space-y-10">
                        <div className="text-center space-y-4">
                            <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em]">Detektyvo Išvados</h4>
                            <div className="bg-emerald-50 rounded-xl md:rounded-[3rem] p-6 md:p-10 border-4 border-white shadow-inner animate-pop-in">
                                <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest text-center mb-4">Ištaisyta Versija</p>
                                <p className="text-xl md:text-4xl text-slate-800 font-black text-center leading-tight tracking-tighter uppercase">
                                    {currentSentence.correct_sentence}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            {currentSentence.words
                                .filter(word => word.needs_capital)
                                .map((word, index) => (
                                    <div key={index} className="bg-white rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 border-2 border-indigo-50 flex flex-col gap-3 shadow-xl animate-pop-in" style={{ animationDelay: `${index * 0.1}s` }}>
                                        <div className="flex items-center gap-4">
                                            <div className="w-8 h-8 md:w-12 md:h-12 bg-indigo-100 rounded-full flex items-center justify-center shrink-0 text-indigo-600 font-black text-sm md:text-xl border-2 md:border-4 border-white shadow-lg">
                                                {index + 1}
                                            </div>
                                            <p className="font-black text-indigo-600 text-sm md:text-xl tracking-tighter uppercase">{word.correct_word}</p>
                                        </div>
                                        <p className="text-slate-500 font-bold text-[10px] md:text-sm leading-relaxed italic font-serif">"{word.reason}"</p>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const targets = (
        <div className="flex gap-3 md:gap-6 justify-center w-full">
            <button
                onClick={() => { triggerHaptic(40); setClickedWords([]); }}
                disabled={clickedWords.length === 0 || !!feedback}
                className="flex-1 py-4 md:py-6 bg-slate-800 text-slate-400 rounded-xl md:rounded-[2.5rem] font-black text-sm md:text-xl border-b-4 md:border-b-8 border-slate-700 hover:bg-slate-700 active:translate-y-1 active:border-b-0 transition-all disabled:opacity-20"
            >
                VALYTI 🗑️
            </button>
            <button
                onClick={checkAnswer}
                disabled={clickedWords.length === 0 || !!feedback}
                className="flex-[2] py-4 md:py-6 px-4 md:px-12 bg-gradient-to-r from-indigo-500 to-indigo-700 text-white rounded-xl md:rounded-[2.5rem] font-black text-sm md:text-2xl shadow-2xl active:translate-y-1 active:border-b-0 transition-all border-b-4 md:border-b-8 border-indigo-900 uppercase tracking-widest"
            >
                TIKRINTI ✓
            </button>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🕵️‍♂️' : '🔎'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKSLUS TYRIMAS!' : 'KAŽKAS PRALEISTA...'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-left italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-700 text-center leading-relaxed">
                                    {feedback === 'correct' ? 'Puiku! Tavo detektyvo akis pastebėjo visus žodžius, kuriems reikia didžiosios raidės. Bylos sprendimas nepriekaištingas!' : 'Oi! Panašu, kad kai kurie įkalčiai buvo praleisti arba pažymėti neteisingai. Peržiūrėk sakinį dar kartą, detektyve!'}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-[10px] md:text-sm font-black text-slate-300 uppercase tracking-[0.3em]">
                                    {feedback === 'correct' ? 'Kita byla...' : 'Tęsiame tyrimą...'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CapitalLetterDetectiveGame;


